'use client'

import {
    createHealthMapQuestion,
    HealthMapQuestion,
    HealthMapQuestionType,
    HEALTH_MAP_QUESTION_TYPE_LABELS,
    updateHealthMapQuestion,
} from '@/lib/api/health-map/health-map-api'
import { useEffect, useState } from 'react'
import { MdAdd, MdClose, MdDelete } from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    /** Tema onde a pergunta nasce. Ausente exige `themes` para o operador escolher. */
    themeId?: number
    themeName?: string
    /**
     * Temas disponíveis, quando o call site não sabe em qual a pergunta entra —
     * é o caso da aba do prontuário, onde o profissional está respondendo e
     * percebe que falta uma pergunta.
     */
    themes?: { id: number; name: string; categoryName?: string }[]
    /** Preenchido = edição. */
    question?: HealthMapQuestion | null
    onClose: () => void
    onSaved: (question: HealthMapQuestion) => void
}

const CHOICE_TYPES: HealthMapQuestionType[] = [
    'SINGLE_CHOICE',
    'MULTIPLE_CHOICE',
]

/**
 * Cadastro de pergunta do Mapa da Saúde.
 *
 * Vive em componente próprio porque é usado nos dois lugares onde uma pergunta
 * nasce: a tela de configuração (montando o questionário) e a própria aba do
 * prontuário (o profissional descobre a pergunta que falta no meio da consulta).
 */
export default function HealthMapQuestionModal({
    themeId,
    themeName,
    themes,
    question,
    onClose,
    onSaved,
}: Props) {
    const isEditing = !!question
    const [selectedThemeId, setSelectedThemeId] = useState<number | null>(
        themeId ?? question?.themeId ?? themes?.[0]?.id ?? null
    )
    const [title, setTitle] = useState('')
    const [helpText, setHelpText] = useState('')
    const [type, setType] = useState<HealthMapQuestionType>('YES_NO')
    const [options, setOptions] = useState<string[]>(['', ''])
    const [required, setRequired] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setTitle(question?.title ?? '')
        setHelpText(question?.helpText ?? '')
        setType(question?.type ?? 'YES_NO')
        setOptions(
            question?.options?.length ? [...question.options] : ['', '']
        )
        setRequired(question?.required ?? false)
    }, [question])

    const needsOptions = CHOICE_TYPES.includes(type)
    const targetThemeId = themeId ?? selectedThemeId

    const handleSave = async () => {
        if (!title.trim()) {
            toast.error('Escreva a pergunta')
            return
        }

        const cleanedOptions = options
            .map(option => option.trim())
            .filter(Boolean)

        if (needsOptions && cleanedOptions.length < 2) {
            toast.error('Perguntas de escolha precisam de ao menos duas alternativas')
            return
        }

        if (!isEditing && !targetThemeId) {
            toast.error('Escolha o tema da pergunta')
            return
        }

        setSaving(true)
        try {
            const payload = {
                title: title.trim(),
                helpText: helpText.trim() || undefined,
                type,
                options: needsOptions ? cleanedOptions : undefined,
                required,
            }

            const result = isEditing
                ? await updateHealthMapQuestion(question!.id, payload)
                : await createHealthMapQuestion({
                      themeId: targetThemeId!,
                      ...payload,
                  })

            if (result.success && result.result) {
                toast.success(isEditing ? 'Pergunta atualizada' : 'Pergunta criada')
                onSaved(result.result)
                onClose()
            } else {
                toast.error(result.error || 'Erro ao salvar a pergunta')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        /* Overlay próprio (sem `modal-box`, que aplica transform): este modal pode
           abrir por cima do modal do prontuário. Ver `prontuario-menu.tsx`. */
        <div
            className="fixed inset-0 z-[920] flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            onClick={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-base-300/50 bg-base-100 shadow-xl">
                <div className="flex items-start justify-between gap-3 border-b border-base-300/60 px-6 py-4">
                    <div className="min-w-0">
                        <h3 className="text-lg font-bold">
                            {isEditing ? 'Editar pergunta' : 'Nova pergunta'}
                        </h3>
                        {themeName && (
                            <p className="truncate text-sm text-base-content/60">
                                Tema: {themeName}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
                    {!isEditing && !themeId && themes && themes.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <label
                                className="text-sm font-medium"
                                htmlFor="health-map-question-theme"
                            >
                                Tema
                            </label>
                            <select
                                id="health-map-question-theme"
                                className="select select-bordered w-full"
                                value={selectedThemeId ?? ''}
                                onChange={event =>
                                    setSelectedThemeId(
                                        Number(event.target.value)
                                    )
                                }
                            >
                                {themes.map(option => (
                                    <option key={option.id} value={option.id}>
                                        {option.categoryName
                                            ? `${option.categoryName} › ${option.name}`
                                            : option.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <label
                            className="text-sm font-medium"
                            htmlFor="health-map-question-title"
                        >
                            Pergunta
                        </label>
                        <textarea
                            id="health-map-question-title"
                            className="textarea textarea-bordered w-full"
                            rows={2}
                            placeholder="Ex.: Com que frequência você pratica atividade física?"
                            value={title}
                            onChange={event => setTitle(event.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label
                            className="text-sm font-medium"
                            htmlFor="health-map-question-help"
                        >
                            Texto de apoio{' '}
                            <span className="font-normal text-base-content/50">
                                (opcional)
                            </span>
                        </label>
                        <input
                            id="health-map-question-help"
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex.: considere as últimas quatro semanas"
                            value={helpText}
                            onChange={event => setHelpText(event.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">
                            Tipo de resposta
                        </label>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {(
                                Object.keys(
                                    HEALTH_MAP_QUESTION_TYPE_LABELS
                                ) as HealthMapQuestionType[]
                            ).map(option => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => setType(option)}
                                    className={`btn btn-sm ${
                                        type === option
                                            ? 'btn-secondary'
                                            : 'btn-outline'
                                    }`}
                                >
                                    {HEALTH_MAP_QUESTION_TYPE_LABELS[option]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {needsOptions && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium">
                                Alternativas
                            </label>
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2"
                                >
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm flex-1"
                                        placeholder={`Alternativa ${index + 1}`}
                                        value={option}
                                        onChange={event =>
                                            setOptions(previous =>
                                                previous.map((item, i) =>
                                                    i === index
                                                        ? event.target.value
                                                        : item
                                                )
                                            )
                                        }
                                    />
                                    {options.length > 2 && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            onClick={() =>
                                                setOptions(previous =>
                                                    previous.filter(
                                                        (_, i) => i !== index
                                                    )
                                                )
                                            }
                                            aria-label="Remover alternativa"
                                        >
                                            <MdDelete className="text-base" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm w-fit gap-1"
                                onClick={() =>
                                    setOptions(previous => [...previous, ''])
                                }
                            >
                                <MdAdd className="text-base" />
                                Adicionar alternativa
                            </button>
                        </div>
                    )}

                    <label className="flex w-fit cursor-pointer items-center gap-3">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-secondary"
                            checked={required}
                            onChange={event => setRequired(event.target.checked)}
                        />
                        <span className="text-sm">
                            Obrigatória — o preenchimento não pode ser concluído
                            sem ela
                        </span>
                    </label>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-base-300/60 px-6 py-4">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-2"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : null}
                        {isEditing ? 'Salvar' : 'Criar pergunta'}
                    </button>
                </div>
            </div>
        </div>
    )
}
